import type { QueryClient } from '@tanstack/react-query';
import {
  removeDeletedProjectElementFromAllCaches,
  toProjectElementType,
} from '@/lib/realtime/project-element-cache-sync';
import {
  removeDeletedResourceLibraryItemFromCache,
  updateResourceLibraryItemNameInCache,
  type ResourceLibraryType,
} from '@/hooks/api/useResourceLibrary';
import { updateMentionResourceNameInCache } from '@/hooks/api/useMentionableResources';
import { setStoryboardBoardListsData } from '@/hooks/api/useStoryboardBoard';
import {
  removeStoryboardBoardResourceItems,
  renameStoryboardBoardResourceItems,
} from '@/lib/storyboard-board-cache';
import { useEditorStore } from '@/stores/editorStore';

const RESOURCE_TYPE_TO_LIBRARY_TYPE: Record<string, ResourceLibraryType> = {
  character: 'character',
  scene: 'location',
  location: 'location',
  prop: 'prop',
  footage: 'footage',
};

function resolveResourceLibraryType(
  resourceType?: string | null,
  elementType?: string | null
): ResourceLibraryType | null {
  const raw = (elementType ?? resourceType)?.trim();
  if (!raw) return null;
  return RESOURCE_TYPE_TO_LIBRARY_TYPE[raw] ?? null;
}

function clearSelectedResourceIfDeleted(deletedId: string): void {
  const { selectedResourceId, selectResource } = useEditorStore.getState();
  if (selectedResourceId === deletedId) {
    selectResource(null, null);
  }
}

export function applyProjectResourceDeletedCacheSync(
  queryClient: QueryClient,
  payload: {
    resourceId?: string | null;
    resourceType?: string | null;
    elementType?: string | null;
  }
): void {
  const resourceId = payload.resourceId?.trim();
  if (!resourceId) return;

  const resourceKind = resolveResourceLibraryType(payload.resourceType, payload.elementType);
  if (!resourceKind) return;

  removeDeletedResourceLibraryItemFromCache(queryClient, { resourceKind, resourceId });
  setStoryboardBoardListsData(queryClient, (old) => {
    if (!old) return old;
    return removeStoryboardBoardResourceItems(old, { resourceId });
  });
  clearSelectedResourceIfDeleted(resourceId);
}

/**
 * 就地无感同步「项目资源已更新（重命名）」：本地批量重命名与协作者实时事件共用。
 * 覆盖资源库列表 / 预览面板（复用列表缓存）/ 分镜板 / mention 候选四个展示面。
 * 返回是否命中并完成就地更新；未命中（缺名或非 footage）返回 false，调用方可回退失活重拉。
 */
export function applyProjectResourceUpdatedCacheSync(
  queryClient: QueryClient,
  payload: {
    resourceId?: string | null;
    resourceType?: string | null;
    elementType?: string | null;
    name?: string | null;
  }
): boolean {
  const resourceId = payload.resourceId?.trim();
  const name = payload.name?.trim();
  if (!resourceId || !name) return false;

  const resourceKind = resolveResourceLibraryType(payload.resourceType, payload.elementType);
  // 目前仅 footage 支持重命名的无感同步；其余类型交由失活重拉
  if (resourceKind !== 'footage') return false;

  updateResourceLibraryItemNameInCache(queryClient, {
    resourceKind,
    resourceId,
    name,
  });
  setStoryboardBoardListsData(queryClient, (old) => {
    if (!old) return old;
    return renameStoryboardBoardResourceItems(old, { resourceId, name });
  });
  updateMentionResourceNameInCache(queryClient, { resourceId, name });
  return true;
}

export function applyProjectElementDeletedCacheSync(
  queryClient: QueryClient,
  payload: {
    projectId?: string | null;
    elementId?: string | null;
    elementType?: string | null;
  }
): void {
  const elementId = payload.elementId?.trim();
  if (!elementId) return;

  const elementType = toProjectElementType(payload.elementType);
  if (!elementType) return;

  removeDeletedProjectElementFromAllCaches(queryClient, {
    elementId,
    elementType,
    projectId: payload.projectId,
  });
  setStoryboardBoardListsData(queryClient, (old) => {
    if (!old) return old;
    return removeStoryboardBoardResourceItems(old, { elementId });
  });
  clearSelectedResourceIfDeleted(elementId);
}
